import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft } from 'lucide-react';

export default function UserDetails() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', id));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Carregar saldo da carteira usando o ID do usuário
          let walletBalance = 0;
          const walletDoc = await getDoc(doc(db, 'wallets', id));
          if (walletDoc.exists()) {
            walletBalance = walletDoc.data().balance || 0;
          }

          setUser({ 
            id: userDoc.id, 
            ...userData,
            walletBalance 
          } as User);
        } else {
          setError('Utilizador não encontrado');
        }
      } catch (err) {
        console.error('Error fetching user:', err);
        setError('Erro ao carregar dados do utilizador');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  if (!currentUser?.role || (currentUser.role !== 'admin' && currentUser.role !== 'gerente')) {
    navigate('/dashboard');
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error || 'Erro ao carregar utilizador'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/dashboard/users')}
              className="text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={24} />
            </button>
            <h2 className="text-2xl font-bold text-secondary">Detalhes do Utilizador</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Informações Pessoais</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-4">
                    {user.photoBase64 ? (
                      <img
                        src={user.photoBase64}
                        alt={user.displayName || user.email}
                        className="h-16 w-16 rounded-full mr-4"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-gray-200 mr-4" />
                    )}
                    <div>
                      <h4 className="font-medium">{user.displayName || user.email.split('@')[0]}</h4>
                      <p className="text-gray-600">{user.email}</p>
                      {user.walletBalance !== undefined && (
                        <p className="mt-2">
                          <span className="font-medium">Saldo da Carteira: </span>
                          <span className="text-green-600">€{user.walletBalance.toFixed(2)}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p>
                      <span className="font-medium">Função: </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.role === 'admin'
                          ? 'bg-red-100 text-red-800'
                          : user.role === 'gerente'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role === 'admin' ? 'Administrador' :
                         user.role === 'gerente' ? 'Gerente' : 'Funcionário'}
                      </span>
                    </p>
                    <p>
                      <span className="font-medium">Estado: </span>
                      {user.role === 'gerente' && (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          user.approvalStatus === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : user.approvalStatus === 'blocked'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {user.approvalStatus === 'approved' ? 'Aprovado' :
                           user.approvalStatus === 'blocked' ? 'Bloqueado' : 'Pendente'}
                        </span>
                      )}
                    </p>
                    <p>
                      <span className="font-medium">Data de Registo: </span>
                      {new Date(user.createdAt).toLocaleDateString('pt-PT')}
                    </p>
                    {user.updatedAt && (
                      <p>
                        <span className="font-medium">Última Atualização: </span>
                        {new Date(user.updatedAt).toLocaleDateString('pt-PT')}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {user.company && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Informações da Empresa</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-4">
                      {user.company.logoBase64 && (
                        <img
                          src={user.company.logoBase64}
                          alt={user.company.name}
                          className="h-16 w-16 rounded-full mr-4"
                        />
                      )}
                      <div>
                        <h4 className="font-medium">{user.company.name}</h4>
                        {user.company.website && (
                          <a
                            href={user.company.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary-dark"
                          >
                            {user.company.website}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      {user.company.address && (
                        <p>
                          <span className="font-medium">Morada: </span>
                          {user.company.address}
                        </p>
                      )}
                      {user.company.phone && (
                        <p>
                          <span className="font-medium">Telefone: </span>
                          {user.company.phone}
                        </p>
                      )}
                      {user.company.vat && (
                        <p>
                          <span className="font-medium">NIF: </span>
                          {user.company.vat}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Dados de Registo</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="space-y-2">
                    {user.fullName && (
                      <p>
                        <span className="font-medium">Nome Completo: </span>
                        {user.fullName}
                      </p>
                    )}
                    {user.companyName && (
                      <p>
                        <span className="font-medium">Nome da Empresa: </span>
                        {user.companyName}
                      </p>
                    )}
                    {user.phone && (
                      <p>
                        <span className="font-medium">Contacto Telefónico: </span>
                        {user.phone}
                      </p>
                    )}
                    <p>
                      <span className="font-medium">Onboarding Concluído: </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.onboardingCompleted
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.onboardingCompleted ? 'Sim' : 'Não'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {user.companyId && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Carteira</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-primary">
                      €{(user.walletBalance || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
